import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CalendarClock, Fingerprint, Settings } from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { Button } from '@/shared/components/ui/Button';
import { useUser } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useTodayLog,
  useTeamAttendance,
  useAttendanceMutations,
} from '../hooks/useAttendance';
import { MyAttendanceCard } from '../components/MyAttendanceCard';
import { TeamAttendance } from '../components/TeamAttendance';
import { OverallAttendanceHistory } from '../components/OverallAttendanceHistory';
import { BiometricDateWiseCard } from '../components/BiometricDateWiseCard';
import { BiometricSettingsModal } from '../components/BiometricSettingsModal';
import { MusterRollGrid } from '../components/MusterRollGrid';
import type { BreakType } from '../types';

export function AttendancePage() {
  const navigate = useNavigate();
  const user = useUser();
  const isPrivileged = ['admin', 'superadmin', 'manager', 'department_admin'].includes(user?.role ?? '') ||
    user?.department === 'hr' || user?.department === 'accounts' || user?.username === 'account';
  const [showBiometricSettings, setShowBiometricSettings] = useState(false);

  const [teamDept, setTeamDept] = useState('all');
  const [teamDate, setTeamDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));

  const { data: today, isLoading: todayLoading } = useTodayLog();
  const { data: team = [], isLoading: teamLoading, isError: teamError } = useTeamAttendance(isPrivileged, teamDept, teamDate);
  const { checkIn, checkOut, startBreak, endBreak } = useAttendanceMutations();

  const busy = checkIn.isPending || checkOut.isPending || startBreak.isPending || endBreak.isPending;

  const handleCheckIn = () =>
    checkIn.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked in at ${d.time}`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to check in.')),
    });

  const handleCheckOut = () =>
    checkOut.mutate(undefined, {
      onSuccess: (d) => toast.success(`Checked out at ${d.time} · ${d.hours}h`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to check out.')),
    });

  const handleStartBreak = (type: BreakType) =>
    startBreak.mutate(type, {
      onSuccess: () => toast.success(`Started ${type} break`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to start break.')),
    });

  const handleEndBreak = () =>
    endBreak.mutate(undefined, {
      onSuccess: (d) => toast.success(`Break ended (${Math.round((d.duration || 0) / 60)} min)`),
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to end break.')),
    });

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={CalendarClock}
        eyebrow="People"
        title="Attendance"
        description={<>Check in, take breaks, and track your hours.</>}
        actions={
          <>{isPrivileged && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate(ROUTES.shiftMasterData)} className="gap-2">
                <Settings className="h-4 w-4" /> Manage Shifts
              </Button>
              <Button variant="secondary" onClick={() => setShowBiometricSettings(true)} className="gap-2">
                <Fingerprint className="h-4 w-4 text-primary" /> Biometric Config (192.0.2.10)
              </Button>
            </div>
          )}</>
        }
      />

      {/* Section 1 — My Attendance (always visible) */}
      <MyAttendanceCard
        today={today}
        loading={todayLoading}
        busy={busy}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
        onStartBreak={handleStartBreak}
        onEndBreak={handleEndBreak}
      />

      {/* Section 2 — Team Daily Attendance (ZYNX app check-ins/outs) */}
      {isPrivileged && (
        <TeamAttendance
          team={team}
          loading={teamLoading}
          isError={teamError}
          selectedDept={teamDept}
          onSelectDept={setTeamDept}
          selectedDate={teamDate}
          onSelectDate={setTeamDate}
        />
      )}

      {/* Section 3 — Overall Attendance & Time Logs History (Daily & historical for all employees) */}
      {isPrivileged && (
        <OverallAttendanceHistory isPrivileged={isPrivileged} />
      )}

      {/* Section 4 — Biometric Hardware Attendance (eSSL 192.0.2.10:4370) */}
      {isPrivileged && <BiometricDateWiseCard />}

      {/* Section 5 — Muster roll grid */}
      {isPrivileged && <MusterRollGrid />}

      {/* Biometric Device Settings Modal */}
      {isPrivileged && (
        <BiometricSettingsModal open={showBiometricSettings} onClose={() => setShowBiometricSettings(false)} />
      )}
    </div>
  );
}
